/**
 * The Postgres side of the line.
 *
 * A sibling of firestore.ts: same interface, same callers, nothing above either
 * of them knows the difference. Which of the two a build uses is one line in
 * index.ts.
 *
 * THE WORK IS THE TRANSLATION, and it lives next door in paths.ts, tested
 * without a database. This file is what remains once the mapping is decided:
 * issuing the queries, nesting an issue's comments back into it, and turning a
 * stream of table changes into the collection the app expects.
 *
 * WHY REALTIME RE-READS RATHER THAN APPLIES A DELTA. Supabase sends the row
 * that changed; Firestore sent the collection. The contract in types.ts says a
 * watcher receives the WHOLE collection each time, deliberately, so that no
 * caller has to hold state to interpret a change — and honouring it here costs
 * one query per change. That is the right trade at this size and it is written
 * down rather than left as a surprise: at a thousand issues a second it would
 * not be, and applying deltas is where to look first.
 *
 * TENANT. Every table carries tenant_id and the app knows nothing about
 * tenants — it was written when there was one workspace. So it is resolved
 * once, from the signed-in person, and added to every read and write. When
 * tenancy proper arrives this is the seam it arrives through.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { nowListening, stopWatching } from '../live';
import type { Backend, BackendError, Rec, ReportedByMe, WithId, WithParent } from './types';
import type { Holiday } from '../work-calendar';
import { target, toColumns, toFields, type Target } from './paths';
import { withLockRetry } from './lock-retry';

/** Postgres's refusal, in the app's words. See BackendError. */
function asBackendError(e: unknown): BackendError {
  const code = (e as { code?: string })?.code;
  const message = (e as { message?: string })?.message ?? String(e);
  const err = new Error(message) as BackendError;
  // 42501 is insufficient privilege; PGRST301 is PostgREST's own refusal. A
  // row-level policy that simply returns nothing is not an error at all, which
  // is the asymmetry the security suite exists to respect.
  err.refused = code === '42501' || code === 'PGRST301' || /permission denied|row-level security/i.test(message);
  err.code = code;
  return err;
}

const fail = (e: unknown): never => {
  throw asBackendError(e);
};

/** The one bucket this app has — see migration 0048. */
/**
 * A NAME NO OTHER LISTENER CAN BE HOLDING — #255.
 *
 * WHAT WENT WRONG. Channels were named after what they watched: `list:projects`,
 * `doc:boards/x`. `client.channel(name)` hands back the EXISTING channel when
 * one of that name is already open, and letting a channel go is
 * `void client.removeChannel(ch)` — started, not waited for. There is nowhere to
 * wait: the thing a listener returns is a cleanup function and React calls it
 * synchronously, so "stop, then start again" genuinely cannot be sequenced.
 *
 * So a listener that restarted before its predecessor had finished going away
 * asked for a name that still existed, was handed a channel that had ALREADY
 * subscribed, and adding a callback to one of those is exactly what Supabase
 * refuses: `cannot add postgres_changes callbacks for realtime:list:projects
 * after subscribe()`.
 *
 * WHY IT COST MORE THAN LIVE UPDATES. The refusal is thrown while the callbacks
 * are being added — before `.subscribe()`, before the function returns. So the
 * listener was never set up AND the code that was setting it up never finished.
 * It surfaced as an unhandled rejection, which is to say: nothing on screen,
 * nothing recorded, and a board that quietly stopped being told anything while
 * looking exactly like one that was.
 *
 * THE FIX IS TO STOP SHARING NAMES AT ALL, rather than to wait for the old one.
 * Every listener takes a number, so a restart cannot collide with its own
 * predecessor no matter how slowly that predecessor goes away. The old channel
 * is still removed — this is not a leak, it is the removal no longer being on
 * the critical path.
 *
 * The name is only an identifier for the socket; nothing subscribes BY it and
 * nothing looks one up, which is what makes this safe. Seen on live and on the
 * test copy on 25 August 2026.
 */
let listeners = 0;
const channelNamed = (what: string) => `${what}#${(listeners += 1)}`;

const FILES = 'documents';

/**
 * ATTACH, AND SAY WHAT HAPPENED — #308.
 *
 * `.subscribe()` was called bare here, and in all three watchers. `SUBSCRIBED`,
 * `CHANNEL_ERROR` and `TIMED_OUT` appeared nowhere in `src/` at all, so a
 * channel that failed to attach and a channel that dropped and came back
 * empty-handed both looked exactly like a board where nothing had changed.
 *
 * TWO THINGS COME OF READING THE STATUS.
 *
 *   1. A REJOIN BACKFILLS. Realtime rejoins a dropped channel on its own and
 *      announces `SUBSCRIBED` again — but everything that changed while it was
 *      away was announced to nobody and is never mentioned. It is all still in
 *      the database. So a second `SUBSCRIBED` re-reads, and the gap closes with
 *      nobody noticing it was there. **This is the fix.**
 *
 *   2. A CHANNEL THAT DOES NOT COME BACK IS SAID OUT LOUD. Nothing can backfill
 *      a listener that never attaches, and a board that is quietly out of date
 *      is worse than one that is visibly broken.
 *
 * ⚠️ THE FIRST `SUBSCRIBED` MUST NOT RE-READ. Every watcher already reads once
 * before it subscribes, so backfilling on the first attach would double every
 * read the app makes, on every board, forever — a fix costing twice the traffic
 * of the bug. `attached` is what tells them apart.
 */
/**
 * WHETHER THE SOCKET IS THERE AT ALL — and why this is a poll, which is not the
 * first thing anybody would reach for.
 *
 * MEASURED, NOT ASSUMED. The status callback below was supposed to be the whole
 * answer: read `CHANNEL_ERROR` and `TIMED_OUT` and say so. Driven with the
 * network turned off underneath a real signed-in page for twenty seconds,
 * **Realtime reported nothing whatsoever** — not `CLOSED`, not `CHANNEL_ERROR`,
 * not `TIMED_OUT`. Silence is exactly what a working board looks like, which is
 * the entire fault #308 is about, arriving one level down.
 *
 * It DOES report `SUBSCRIBED` again when it rejoins, which is why the backfill
 * works and is the half that matters. But nothing announces the going away, so
 * nothing can be told about it by listening.
 *
 * SO THE SOCKET IS ASKED. Every few seconds, cheaply, in memory — no request —
 * and the browser is asked too, because measuring showed the socket is the
 * slower of the two by a wide margin. See `connected` below.
 *
 * `?? true` on the client reading, deliberately: if a future version drops
 * `isConnected`, this must go quiet rather than tell everybody their board is
 * broken. A false alarm here is worse than no alarm, because it is the alarm
 * people learn to ignore.
 */
const THE_SOCKET = 'the socket itself';
let heartbeat: ReturnType<typeof setInterval> | undefined;
/**
 * Force the realtime socket to reconnect and re-evaluate at once — #414.
 *
 * The heartbeat only READS `isConnected()`; nothing here ever asks the socket to
 * reconnect, because Realtime is supposed to do that itself. After a laptop
 * sleeps it often does not — the socket sits closed, `isConnected()` stays false,
 * and the "reload to reconnect" notice stays up even after a return-to-tab sweep
 * has already backfilled the board and made it current. Set by watchTheSocket
 * (it holds the client and the `connected` reading) and called by the visibility
 * sweep, so returning to the tab reconnects the socket rather than only re-reading.
 */
let refreshSocket: (() => void) | undefined;
function watchTheSocket(client: {
  realtime?: {
    isConnected?: () => boolean;
    connect?: () => void;
    disconnect?: () => Promise<unknown> | void;
  };
}): void {
  if (heartbeat) return;
  const connected = () => {
    try {
      /*
       * BOTH, AND MEASURED RATHER THAN CHOSEN. Driven with the network off
       * under a real page: `isConnected()` STAYED TRUE for the whole twenty
       * seconds — the client leans on heartbeat timeouts and takes far longer
       * than that to admit anything — while `navigator.onLine` went false at
       * once. Neither is sufficient alone.
       *
       * `onLine` IS ONLY TRUSTED IN ONE DIRECTION, which is the direction that
       * is safe. True means "there is a network interface", which is famously
       * not the same as "the server is reachable", so it is never taken as
       * proof that anything is being delivered. False means there is no network
       * at all, and that IS proof that nothing is.
       */
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
      return client.realtime?.isConnected?.() ?? true;
    } catch {
      return true;
    }
  };
  heartbeat = setInterval(() => nowListening(THE_SOCKET, connected()), 5000);
  /*
   * The reconnect the return-to-tab sweep leans on (#414).
   *
   * ⚠️ A BARE `connect()` IS NOT ENOUGH, and shipping one is what left the notice
   * stuck. Reproduced on the real client, 23 September 2026: after the socket is
   * left a zombie — the state a slept laptop leaves, still reported `connecting`
   * or `closing` and never progressing — `realtime.connect()` RETURNS WITHOUT
   * DOING ANYTHING. It skips whenever the socket is `isConnecting()`,
   * `isDisconnecting()` or `isConnected()` (RealtimeClient guards on exactly
   * those three), so it only ever opens a socket that is already cleanly closed.
   * The zombie is none of those, so `connect()` no-ops, `isConnected()` stays
   * false forever, and the board's data catches up on the sweep while the
   * "reload to reconnect" notice sits on top of it until the page is reloaded.
   *
   * So tear the socket down for REAL first, then connect. `disconnect()` takes it
   * to a clean `closed`, from which `connect()` opens a fresh socket that rejoins
   * every channel — each SUBSCRIBED clears its own entry and backfills. Proven in
   * the same session: disconnect→connect brought all 21 channels back and the
   * notice cleared, where a bare connect() left every one of them down.
   *
   * ONLY WHEN IT IS ACTUALLY DOWN. A healthy socket is left alone — tearing one
   * down on every ordinary tab switch would make attention itself the outage.
   */
  const reread = () => nowListening(THE_SOCKET, connected());
  refreshSocket = () => {
    const rt = client.realtime;
    if (!rt || rt.isConnected?.()) {
      reread();
      return;
    }
    Promise.resolve()
      .then(() => rt.disconnect?.())
      .catch(() => {
        /* Best-effort — the connect below and the heartbeat are the backstops. */
      })
      .then(() => {
        try {
          rt.connect?.();
        } catch {
          /* Same. */
        }
        reread();
      });
    /* The fresh socket opens asynchronously, so the read above still sees it down;
       the 5s heartbeat is the backstop, and these bounded re-reads clear the notice
       within about a second in the ordinary case. Guarded — the suite has no window. */
    if (typeof window !== 'undefined') {
      for (const ms of [300, 900, 2000]) setTimeout(reread, ms);
    }
  };
  /*
   * The two events are why five seconds is not the worst case: going offline
   * and coming back are announced, so the poll is the backstop rather than the
   * mechanism.
   *
   * GUARDED, BECAUSE THIS FILE IS IMPORTED BY THE SUITE. `test-live-updates`
   * loads the backend in Node to prove a listener attaches, and there is no
   * `window` there — six checks went red on one unguarded line.
   */
  if (typeof window !== 'undefined') {
    window.addEventListener('offline', () => nowListening(THE_SOCKET, false));
    window.addEventListener('online', () => nowListening(THE_SOCKET, connected()));
  }
}

/**
 * ── CATCHING UP A BOARD THAT WENT QUIET WHILE YOU WERE AWAY — #379 ────────────
 *
 * #308 backfills a channel that DROPS and rejoins. But a channel can stop
 * delivering WITHOUT dropping — the socket still reports connected, no error is
 * raised, no rejoin fires — and #308's own measuring proved `isConnected()`
 * stays true throughout. So a board left in the background can go silently
 * stale, with nothing here to ask again and nothing to raise the notice.
 *
 * The one moment we KNOW somebody cares the board is current is when they come
 * back to it. So every listener's re-read is registered here, and when the tab
 * is shown again or the window regains focus, all of them catch up at once — the
 * same backfill #308 does on rejoin, triggered by attention rather than by a
 * reconnect we cannot rely on being announced. Counted, not named, so a listener
 * added later joins the sweep without a second place to remember it.
 */
const catchUp = new Map<string, () => void>();
let watchingVisibility = false;
function watchVisibility(): void {
  if (watchingVisibility) return;
  /* GUARDED, like watchTheSocket: the suite loads this file in Node, where
     there is no document or window. */
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  watchingVisibility = true;
  let pending: ReturnType<typeof setTimeout> | undefined;
  const sweep = () => {
    /* Only when the page is actually in front of somebody — a focus fired while
       hidden is not attention on the board. */
    if (document.visibilityState !== 'visible') return;
    /* Coalesce focus and visibilitychange firing together into one sweep. */
    clearTimeout(pending);
    pending = setTimeout(() => {
      /* First put the connection back — a socket that went down while the tab
         was hidden will not reconnect on its own, and until it does the notice
         stays up on a board this sweep is about to make current (#414). */
      refreshSocket?.();
      for (const again of catchUp.values()) {
        try {
          again();
        } catch {
          /* A re-read that throws must not become the outage — the same rule as
             nowListening's loop. */
        }
      }
    }, 50);
  };
  document.addEventListener('visibilitychange', sweep);
  window.addEventListener('focus', sweep);
}

/** For the suite, which has to start each case from the same place. */
export function forgetCatchUp(): void {
  catchUp.clear();
  /* Also drop the once-only guard, so a case that installs its own document/
     window sees the sweep re-register against it rather than an earlier one. */
  watchingVisibility = false;
}

/**
 * For the suite: forget the once-only socket watch, so a case can attach its own
 * client and see watchTheSocket run fresh against it (the heartbeat and the
 * reconnect hook are otherwise wired to whichever client attached first).
 */
export function forgetSocketWatch(): void {
  if (heartbeat) clearInterval(heartbeat);
  heartbeat = undefined;
  refreshSocket = undefined;
}

function listenFor(
  ch: { subscribe: (cb: (status: string) => void) => unknown },
  name: string,
  again: () => void,
  client?: { realtime?: { isConnected?: () => boolean; connect?: () => void } }
): void {
  if (client) watchTheSocket(client);
  /* Register this listener's re-read so a return to the tab can catch it up,
     and let it go again in the teardown that calls stopWatching(name). */
  catchUp.set(name, again);
  watchVisibility();
  let attached = false;
  ch.subscribe((status: string) => {
    if (status === 'SUBSCRIBED') {
      nowListening(name, true);
      /* Everything it missed is still in the database, waiting to be asked. */
      if (attached) again();
      attached = true;
      return;
    }
    /*
     * `CLOSED` IS NOT LISTED HERE, and that is deliberate. It is what an
     * ordinary teardown looks like — switching board closes every channel it
     * had — so treating it as an outage would put the notice on screen every
     * time somebody changed board, which is the fastest way to teach people to
     * ignore it.
     */
    if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') nowListening(name, false);
  });
}

export function supabaseBackend(
  client: SupabaseClient,
  id: (kind: string, key: string) => string
): Backend {
  /**
   * Resolved once, and FORGOTTEN WHENEVER THE TOKEN CHANGES.
   *
   * The read policy on `people` is `tenant_id = my_tenant()`, so this cannot
   * return the wrong workspace — the database will not show it any other. But
   * it CAN return one that was right when it was asked, and then go stale:
   * switching writes the choice into the token and renews the session, and a
   * value cached before that names where they used to be.
   *
   * READS WOULD BE FINE — they are filtered by the policies, which have moved
   * on. It is WRITES that carry this value, and a write stamped with the old
   * workspace is refused by the very rule that keeps workspaces apart. The
   * store fires writes without watching them, so the refusal goes nowhere:
   * somebody switches workspace, raises an issue, and it silently does not
   * save. Proved by the last two cases in test-switching.ts, which write after
   * switching rather than only reading — an earlier version of that test only
   * read, and passed happily with this line deleted.
   *
   * Cleared on the token changing rather than by whoever does the switching,
   * because then it holds for any reason a session is renewed — a switch, an
   * hourly refresh, signing in as somebody else in the same tab.
   */
  let tenant: string | null = null;
  client.auth.onAuthStateChange(() => {
    tenant = null;
  });

  /**
   * ── NOTHING IS ASKED UNTIL THERE IS SOMEBODY TO ASK ABOUT — #297 ─────────
   *
   * `getSession()` resolves once the client has finished loading and, if need
   * be, refreshing the sign-in from storage. Before that it has no token to
   * attach, so a query goes out unauthenticated and the database answers 401 —
   * "not authenticated" — to a question about who you are.
   *
   * A witness reproduced exactly that on 26 August 2026: four 401s in about a
   * dozen sign-ins, on `GET /people?select=email&limit=1`. Four in twelve is a
   * race, not a fluke, and it gets WORSE on a slow connection — the opposite of
   * how it presents to whoever is testing on a fast one.
   *
   * It is cheap to wait for: after the client has initialised this returns from
   * memory, so the cost is paid once and only where it was already being paid.
   */
  const sessionAttached = () => client.auth.getSession();

  async function tenantId(): Promise<string> {
    if (tenant) return tenant;
    /* The same race, on the value every WRITE is stamped with — and a write
       refused for a stale stamp is the silent kind. Held to the same gate. */
    await sessionAttached();
    const { data, error } = await client.from('people').select('tenant_id').limit(1).maybeSingle();
    if (error) fail(error);
    if (!data) fail({ code: '42501', message: 'not a member of any workspace' });
    tenant = (data as { tenant_id: string }).tenant_id;
    return tenant;
  }

  /*
   * `.eq()` for each part of the key, keeping the builder's own type so the
   * result can still be awaited. Written generically rather than typed to a
   * narrow shape: the narrow version compiled and then refused `.maybeSingle()`
   * on its own return value, which is the type system correctly objecting to a
   * lie about what a query builder is.
   */
  const where = <T>(q: T, w: Record<string, unknown>): T => {
    let out = q as unknown as { eq: (c: string, v: unknown) => unknown };
    for (const [k, v] of Object.entries(w)) out = out.eq(k, v) as typeof out;
    return out as unknown as T;
  };

  /*
   * READ A WHOLE GROUP TABLE, PAGED past PostgREST's 1000-row cap.
   *
   * A bare `.select('*')` stops at 1000 rows and drops the rest SILENTLY. This
   * read feeds the portfolio and My Work (`issues` alone runs to thousands in a
   * busy tenant), so the cap was a MONEY cap — a dropped card is dropped from the
   * card count, the Project % and the EAC, while the screen looks complete. The
   * one definition lives here so every group read pages the same way; the split
   * where one reader paged and another did not is exactly how the truncation hid.
   * Ordered by the unique `id` (present on every group table) so the page
   * boundaries are stable, and read to a short batch so nothing is left behind.
   */
  const pageGroup = async (name: string): Promise<Rec[]> => {
    const PAGE = 1000;
    const rows: Rec[] = [];
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await client
        .from(name)
        .select('*')
        .order('id', { ascending: true })
        .range(from, from + PAGE - 1);
      if (error) fail(error);
      const batch = (data ?? []) as Rec[];
      rows.push(...batch);
      if (batch.length < PAGE) break;
    }
    return rows;
  };

  /**
   * AN ISSUE'S COMMENTS AND HISTORY, PUT BACK INSIDE IT.
   *
   * They are rows here and were arrays there, and the app still reads
   * `issue.comments`. Nesting on the way out is what lets the store stay
   * exactly as it is while the storage underneath is a straight improvement —
   * an insert cannot race, which is what `arrayUnion` existed to paper over.
   */
  /**
   * WHO MAY OPEN A BOARD, PUT BACK INSIDE IT.
   *
   * The app reads `project.members` and `project.groups` as arrays on the board
   * itself, which is the shape Firestore held. Here they are rows in
   * `board_people` and `board_groups` — and that is not a tidier way to store
   * the same thing, it is the ONLY thing the database will listen to. The
   * policies read those two tables to decide who may open a board.
   *
   * The seam never knew about them, so boards came back with no lists at all
   * and every board read as open to everyone while Postgres was quietly
   * enforcing something narrower. A screen disagreeing with the database about
   * who may see what is the worst kind of wrong: both halves look like they are
   * working.
   */
  /**
   * A BOARD NAMED INSIDE A RECORD, NOT IN ITS PATH.
   *
   * Ids get hashed into uuids on the way through — `main` becomes a uuid — and
   * that happens in `target()`, which reads the PATH. An audit line carries the
   * board it concerns as a FIELD instead, so it never went past that: `main`
   * was handed straight to a uuid column and refused.
   *
   * Only audit does this. It is written out as a special case rather than made
   * general, because a rule inferring which fields are secretly ids would be
   * cleverer than it is safe.
   */
  function asIds(rec: Rec, table: string): Rec {
    if (table !== 'audit' || typeof rec.projectId !== 'string') return rec;
    return { ...rec, projectId: id('board', rec.projectId) };
  }

  async function nestBoards(rows: Rec[]): Promise<Rec[]> {
    if (rows.length === 0) return rows;
    const ids = rows.map((r) => r.id as string);
    const [{ data: ps }, { data: gs }] = await Promise.all([
      client.from('board_people').select('board_id, email').in('board_id', ids),
      client.from('board_groups').select('board_id, group_id').in('board_id', ids),
    ]);
    const gather = (list: Rec[] | null, field: string) => {
      const map = new Map<string, string[]>();
      for (const r of list ?? []) {
        const k = r.board_id as string;
        (map.get(k) ?? map.set(k, []).get(k)!).push(String(r[field]));
      }
      return map;
    };
    const people = gather(ps as Rec[] | null, 'email');
    const groups = gather(gs as Rec[] | null, 'group_id');
    return rows.map((r) => ({
      ...r,
      members: people.get(r.id as string) ?? [],
      groups: groups.get(r.id as string) ?? [],
    }));
  }

  /**
   * The lists as rows, replacing whatever was there.
   *
   * Replaced rather than merged because the app hands over the WHOLE list every
   * time — it is rewriting who may open the board, not adding to it, and
   * merging would make removing somebody impossible.
   */
  async function replaceBoardLists(boardId: string, members?: unknown, groups?: unknown) {
    const tid = await tenantId();

    /*
     * ONLY PEOPLE WHO ARE ACTUALLY IN THE WORKSPACE.
     *
     * `board_people` has a foreign key to `people`, and it is right to: naming
     * somebody on a board who cannot sign in at all means nothing. Firestore
     * enforced no such thing, so boards carry addresses from people long gone —
     * one board still lists rrobinson@ragkrconsulting.com.
     *
     * The app rewrites the WHOLE list every time somebody is added, so every
     * stale address went in with it and Postgres refused the lot: "violates
     * foreign key constraint board_people_tenant_id_email_fkey". Adding one
     * person failed because of somebody who left.
     *
     * Dropping them is not a loss of meaning. `can_open` grants access through
     * this table, and a row here for somebody with no `people` row grants
     * nothing — they cannot get past the front door to use it. So the list
     * becomes what it always effectively was.
     */
    if (Array.isArray(members) && members.length) {
      const wanted = members.map((m) => String(m).toLowerCase());
      const { data: real } = await client.from('people').select('email').in('email', wanted);
      const known = new Set((real ?? []).map((r) => String((r as Rec).email).toLowerCase()));
      members = wanted.filter((m) => known.has(m));
    }

    for (const [table, column, list] of [
      ['board_people', 'email', members],
      ['board_groups', 'group_id', groups],
    ] as const) {
      if (!Array.isArray(list)) continue;
      await client.from(table).delete().eq('tenant_id', tid).eq('board_id', boardId);
      if (list.length) {
        const { error } = await client.from(table).insert(
          list.map((v) => ({ tenant_id: tid, board_id: boardId, [column]: String(v) }))
        );
        if (error) fail(error);
      }
    }
  }

  async function nest(rows: Rec[], t: string): Promise<Rec[]> {
    if (t === 'boards') return nestBoards(rows);
    /*
     * A BUILD'S LABEL, PUT BACK. There is no id column — a build is its number
     * on its board — and the app keys the release list by `build.id`, so every
     * card was rendering with an undefined key. React tolerates that and
     * quietly stops matching cards to builds when the list changes.
     */
    if (t === 'builds') return rows.map((r) => ({ ...r, id: `b${r.build}` }));
    if (t !== 'issues' || rows.length === 0) return rows;
    const ids = rows.map((r) => r.id as string);
    /*
     * READ EVERY ROW, NOT JUST THE FIRST PAGE. A single `.select()` returns at
     * most the project's "Max rows" (1000 by default), and this fetches the
     * comments and the activity for EVERY card on the board at once. Once a busy
     * board's history passed that cap the request came back truncated, in
     * arbitrary order — so a card's recent moves (in-review → done) could simply
     * be missing from what the history read, though the rows were safe in the
     * table. Page by the primary key until a short page ends it; the per-card
     * sort below still orders what we return by time. Most boards fit in one page,
     * so this is one request as before and only grows for the boards that need it.
     *
     * AND CHUNK THE IDS, so the REQUEST fits too. `.in('issue_id', ids)` puts
     * every card's id in the query string, and a board with enough cards (the
     * dev board passed ~400) makes that URL longer than the server will accept
     * — the read then fails outright with `fetch failed`, not truncation. This
     * was the wrong half of the same worry the page-loop above fixed: that one
     * guards the RESPONSE size, this guards the REQUEST size. Ask for the ids in
     * batches and concatenate; the per-card grouping and sort below do not care
     * which request a row arrived in. A small board is still one batch.
     */
    const PAGE = 1000;
    const IDS_PER_REQUEST = 150;
    const idChunks: string[][] = [];
    for (let i = 0; i < ids.length; i += IDS_PER_REQUEST) {
      idChunks.push(ids.slice(i, i + IDS_PER_REQUEST));
    }
    /*
     * READ THE CHUNKS IN PARALLEL, not one after another (#7a, 21 Oct). The id
     * list is chunked to keep each REQUEST short (above); reading those chunks
     * serially made a board's children a stack of round-trips deep — a 348-card
     * board is three chunks, so three sequential waits per table, every board
     * open AND every realtime re-read. The chunks are independent, and the
     * grouping + per-card sort below do not care which request a row arrived in,
     * so fire them together: the read is now one round-trip deep for any board
     * that fits the page size (almost all), and only the rare board past a
     * chunk's 1000-row page pays a second, still-parallel wait. `select('*')`
     * is unchanged on purpose — trimming to named columns is a separate step
     * that needs a column-by-column audit so no field the app reads is dropped.
     */
    const pageChunk = async (table: 'comments' | 'activity', chunk: string[]): Promise<Rec[]> => {
      const out: Rec[] = [];
      for (let from = 0; ; from += PAGE) {
        let q = client.from(table).select('*').in('issue_id', chunk);
        if (table === 'comments') q = q.is('removed_at', null);
        const { data, error } = await q.order('id').range(from, from + PAGE - 1);
        if (error) fail(error);
        const batch = (data ?? []) as Rec[];
        out.push(...batch);
        if (batch.length < PAGE) break;
      }
      return out;
    };
    const pageAll = async (table: 'comments' | 'activity'): Promise<Rec[]> =>
      (await Promise.all(idChunks.map((chunk) => pageChunk(table, chunk)))).flat();
    const [cs, as] = await Promise.all([pageAll('comments'), pageAll('activity')]);
    const by = (list: Rec[] | null, table: string) => {
      const map = new Map<string, Rec[]>();
      for (const r of list ?? []) {
        const k = r.issue_id as string;
        (map.get(k) ?? map.set(k, []).get(k)!).push(toFields(r, table));
      }
      return map;
    };
    const comments = by(cs, 'comments');
    const activity = by(as, 'activity');
    return rows.map((r) => ({
      ...r,
      comments: (comments.get(r.id as string) ?? []).sort(
        (a, b) => Number(a.createdAt) - Number(b.createdAt)
      ),
      activity: (activity.get(r.id as string) ?? []).sort((a, b) => Number(a.at) - Number(b.at)),
    }));
  }

  /**
   * The people table, read the way the access document was.
   *
   * ASSISTANTS ARE LEFT OUT, and this is the line that does it. They hold real
   * accounts with real ranks and the policies treat them exactly like anybody
   * else — see migration 0025 — but they are set up and listed in the
   * Assistants tab, so a coding tool does not sit in People and access between
   * two colleagues.
   *
   * The cost was argued before it was accepted: this screen is no longer the
   * complete answer to "who can write here". `select email from people where
   * is_assistant` is, and the migration says so in as many words.
   *
   * WRITING BACK IS SAFE DESPITE THE FILTER. `writeAccess` deletes whoever is
   * missing from the list it is handed, so a filtered read plus a full write
   * would delete every assistant the moment anybody edited the guest list.
   * That is why the delete below is scoped `is_assistant = false` rather than
   * relying on this list being complete.
   */
  async function readAccess(): Promise<Rec> {
    const { data, error } = await client
      .from('people')
      .select('email, is_admin')
      .eq('is_assistant', false);
    if (error) fail(error);
    const rows = (data ?? []) as { email: string; is_admin: boolean }[];
    return {
      emails: rows.map((r) => r.email),
      admins: rows.filter((r) => r.is_admin).map((r) => r.email),
    };
  }

  /**
   * AND WRITTEN BACK AS A DIFFERENCE, because the app hands over the whole
   * list and Postgres holds rows. Somebody removed from the list is deleted;
   * somebody new is added; everybody else has their admin flag set to what the
   * list says. Doing it as "delete all, insert all" would work and would take
   * every person's name and role with it.
   */
  async function writeAccess(rec: Rec): Promise<void> {
    const t = await tenantId();
    const emails = ((rec.emails as string[]) ?? []).map((e) => e.toLowerCase());
    const admins = new Set(((rec.admins as string[]) ?? []).map((a) => a.toLowerCase()));
    /*
     * ASSISTANTS ARE NOT IN THE LIST BEING HANDED IN, because readAccess leaves
     * them out — so asking "who is here that the list does not mention" would
     * name every one of them, and the delete below would remove them. Saving
     * the People screen would silently revoke the tracker push.
     *
     * Scoped here rather than trusted from the caller: the list arrives from a
     * screen, and a screen that forgets to include something is exactly the
     * failure this guards against.
     */
    const { data } = await client.from('people').select('email, is_admin').eq('is_assistant', false);
    const have = new Map(((data ?? []) as { email: string; is_admin: boolean }[]).map((r) => [r.email, !!r.is_admin]));

    const gone = [...have.keys()].filter((e) => !emails.includes(e));
    if (gone.length) {
      const { error } = await client.from('people').delete().in('email', gone);
      if (error) fail(error);
    }
    /*
     * ONLY THE ROWS THAT MOVED, IN ONE REQUEST. The screen hands over the whole
     * list, but writing every row back meant one upsert per person — N sequential
     * round trips for a single toggle, so the list took headcount-many round
     * trips to settle and the change showed minutes late on a populated
     * workspace. A person who is new, or whose admin flag now differs from the
     * stored row, is the only one that needs writing; everybody else already
     * matches. Upserted together, so the cost is one round trip however long the
     * list, and the change trigger fires only on the rows that actually changed.
     */
    const changed = emails
      .filter((e) => !have.has(e) || have.get(e) !== admins.has(e))
      .map((email) => ({ tenant_id: t, email, is_admin: admins.has(email) }));
    if (changed.length) {
      const { error } = await client.from('people').upsert(changed, { onConflict: 'tenant_id,email' });
      if (error) fail(error);
    }
  }

  /** One person's row, whichever of its shapes was asked for. */
  async function readPerson(email: string, as: 'role' | 'profile'): Promise<Rec | null> {
    const { data, error } = await client.from('people').select('*').eq('email', email).maybeSingle();
    if (error) fail(error);
    if (!data) return null;
    const p = toFields(data as Rec);
    return as === 'role' ? { role: p.defaultRole } : p;
  }

  async function writePerson(email: string, rec: Rec, as: 'role' | 'profile'): Promise<void> {
    const t = await tenantId();
    /*
     * `name` IS THE JOINED FORM OF THE TWO HALVES, AND THERE IS NO COLUMN FOR IT.
     *
     * The account screen writes all three at once — firstName, lastName, and
     * `name` as the two put together — because Firestore held whichever the
     * person had. Handed to Postgres, `name` is a column that does not exist
     * and the WHOLE write is refused. The store fires it with `void`, so
     * nothing was thrown anywhere anybody could see it: typing your name and
     * watching it come back the same afterwards, for ever.
     *
     * Dropped here, and used to fill the halves if they are the ones missing —
     * which is the same rule the migration follows for the same reason. Any
     * OTHER unknown column still fails loudly, because a field nobody mapped
     * is a field being lost and that should be noisy.
     */
    const { name, ...rest } = rec;
    const halves =
      typeof name === 'string' && !rec.firstName && !rec.lastName
        ? (() => {
            const parts = name.trim().split(/\s+/).filter(Boolean);
            return parts.length ? { firstName: parts[0], lastName: parts.slice(1).join(' ') } : {};
          })()
        : {};
    const patch = as === 'role' ? { default_role: rec.role } : toColumns({ ...rest, ...halves });

    /*
     * AN UPDATE FIRST, AND THIS IS NOT A REFINEMENT — IT IS THE ONLY WAY MOST
     * PEOPLE CAN GET IN AT ALL.
     *
     * A person may change their OWN row: `people_self` permits it, provided they
     * leave their role and their admin flag alone. But an UPSERT is an insert
     * that falls back to an update, and Postgres checks the INSERT permission
     * either way — which only an admin has. So a non-admin accepting the terms
     * was refused, and since the terms are a GATE, every non-admin was locked
     * out of the product for good. Nobody had noticed because the only two
     * accounts that had ever accepted them were admins.
     *
     * Updating first means the ordinary case — changing something about a person
     * who already exists — needs only the permission it actually requires.
     */
    const { data: changed, error: updateErr } = await client
      .from('people')
      .update(patch)
      .eq('tenant_id', t)
      .eq('email', email)
      .select('email');
    if (updateErr) fail(updateErr);
    if (changed?.length) return;

    /*
     * Nobody of that name yet, so this really is an insert — which only an admin
     * may do, and which is refused loudly rather than silently if they are not.
     */
    const { error } = await client
      .from('people')
      .upsert({ tenant_id: t, email, ...patch }, { onConflict: 'tenant_id,email' });
    if (error) fail(error);
  }

  async function readOne(where_: Target): Promise<Rec | null> {
    switch (where_.kind) {
      case 'access':
        return readAccess();
      case 'role':
        return readPerson(where_.email, 'role');
      case 'profile':
        return readPerson(where_.email, 'profile');
      case 'boardRole': {
        const { data } = await client
          .from('board_roles')
          .select('role')
          .eq('email', where_.email)
          .eq('board_id', where_.board)
          .maybeSingle();
        return data ? (data as Rec) : null;
      }
      case 'row': {
        const { data, error } = await where(
          client.from(where_.table).select('*'),
          where_.where
        ).maybeSingle();
        if (error) fail(error);
        if (!data) return null;
        const [nested] = await nest([data as Rec], where_.table);
        return toFields(nested, where_.table);
      }
      default:
        fail({ message: `readOne cannot read a collection` });
        return null;
    }
  }

  /**
   * `narrow` IS ASKED OF THE DATABASE, not applied afterwards.
   *
   * The listener's filter used to be a `rows.filter(...)` on the way back, so
   * the query itself said `select *`. The comment on the sessions listener
   * claimed the opposite — "narrowed to their own address… a broader query is
   * refused outright" — and had been wrong for as long as it had been there. A
   * witness caught it on 26 August 2026.
   *
   * THIS IS ABOUT THE LISTENERS THAT ASK FOR A FILTER, which is the sessions
   * one. The audit listener passes none ON PURPOSE — the History tab under
   * People and access shows the whole workspace's trail, so an admin genuinely
   * needs all of it, and everybody else is held to their own rows by the policy
   * rather than by the query. Narrowing that one would break the screen it
   * feeds. Said here because "the query is unfiltered" is true of both and a
   * fault in only one.
   *
   * The field is the APP's name and the column may not be — `who` is `email` on
   * a session — so it goes through toColumns rather than being used raw. That
   * mapping is the one the writes use, so the two cannot drift.
   *
   * THE CLIENT-SIDE FILTER STAYS. It is not redundant: the same witness forged
   * a server response containing somebody else's rows and neither panel drew
   * them, precisely because of it. Asking the database properly is about not
   * fetching what nobody needs; the filter afterwards is about not trusting
   * what came back.
   */
  async function readMany(
    where_: Target,
    narrow?: { field: string; equals: unknown }
  ): Promise<WithId[]> {
    switch (where_.kind) {
      case 'roles': {
        const { data } = await client.from('people').select('email, default_role');
        return ((data ?? []) as Rec[]).map((r) => ({ id: r.email as string, role: r.default_role }));
      }
      case 'profiles': {
        const { data } = await client.from('people').select('*');
        return ((data ?? []) as Rec[]).map((r) => ({ ...toFields(r), id: r.email as string }));
      }
      case 'boardRoles': {
        const { data } = await client.from('board_roles').select('*');
        return ((data ?? []) as Rec[]).map((r) => ({ id: r.board_id as string, role: r.role }));
      }
      case 'rows': {
        const narrowed = narrow
          ? toColumns({ [narrow.field]: narrow.equals }, where_.table)
          : {};
        const { data, error } = await where(client.from(where_.table).select('*'), {
          ...where_.where,
          ...narrowed,
        });
        if (error) fail(error);
        const nested = await nest((data ?? []) as Rec[], where_.table);
        return nested.map((r) => toFields(r, where_.table) as WithId);
      }
      default:
        fail({ message: 'readMany cannot read a document' });
        return [];
    }
  }

  /*
   * ── HOW MANY TIMES THIS BROWSER HAS CHANGED SOMETHING ─────────────────────
   *
   * Counted so that a read can tell whether it is out of date.
   *
   * A read asks the database for the list as it stands. If somebody presses Add
   * a moment later, the answer coming back was taken BEFORE that press and
   * knows nothing about it — so handing it to the screen puts the card back
   * where it was. Then the write's own change arrives, another read runs, and
   * the card leaves again.
   *
   * That is the flicker exactly: "you add it and take it back out, it
   * disappears and reappears in the sprint and then disappears again."
   * (Vinnie, 19 August 2026.) Nothing was wrong with the data — the screen was
   * being told the truth twice, in the wrong order.
   *
   * Every listener notes this number when it asks, and checks it again when the
   * answer arrives. If it has moved, the answer predates a change made here and
   * is thrown away rather than shown.
   */
  /*
   * COUNTED PER TABLE, NOT ONE NUMBER FOR THE WHOLE APP.
   *
   * A single global count made every listener distrust its answer whenever ANY
   * write was in flight — so a board open, which fires a handful of unrelated
   * writes (the sign-in stamp, this device's session row, last-seen), had each
   * of its ~24 collection reads discarded and re-asked several times over. The
   * fault was measured on 21 Oct: ~5x read amplification, a board's own data
   * read four to six times on every open, stretched over ~9s by the browser's
   * six-connection limit. A write to `sessions` cannot have changed what the
   * `issues` list shows, so it must not throw the issues read away.
   *
   * So the count is kept per table. A listener checks only the tables it
   * actually reads (its own, plus anything nested into it — a card carries its
   * comments and history), and ignores writes to anything else. The flicker
   * this exists to prevent is a write and a read of the SAME list racing, and
   * that is still caught exactly: adding a card writes `issues`, and the issues
   * listener reads `issues`.
   */
  const writesBy = new Map<string, number>();
  const inFlightBy = new Map<string, number>();
  /** The table a write lands in, whatever shape its path took. access/role/
      profile all rewrite `people`; a board role is `board_roles`; everything
      else carries its own table. */
  const tableFromTarget = (t: Target): string =>
    t.kind === 'boardRole' || t.kind === 'boardRoles'
      ? 'board_roles'
      : t.kind === 'access' ||
          t.kind === 'role' ||
          t.kind === 'roles' ||
          t.kind === 'profile' ||
          t.kind === 'profiles'
        ? 'people'
        : 'table' in t
          ? t.table
          : '';
  const tableOf = (path: string): string => {
    try {
      return tableFromTarget(target(path, id));
    } catch {
      return '';
    }
  };
  /** Called the moment a change is SENT, not when it is confirmed — the point
      is to invalidate answers to THIS table that were already in flight by then. */
  const changing = (table: string) => {
    writesBy.set(table, (writesBy.get(table) ?? 0) + 1);
    inFlightBy.set(table, (inFlightBy.get(table) ?? 0) + 1);
  };
  /** And when it has been answered, however it was answered. A refused change
      still ends: leaving it counted would silence every listener for good. */
  const changed = (table: string) => {
    inFlightBy.set(table, Math.max(0, (inFlightBy.get(table) ?? 0) - 1));
  };
  /** The write-race guard, read over the set of tables a listener reads: how
      many writes to any of them have happened, and whether any is still in
      flight. A write to a table outside the set is invisible to that listener. */
  const writesOn = (tables: string[]) =>
    tables.reduce((n, t) => n + (writesBy.get(t) ?? 0), 0);
  const inFlightOn = (tables: string[]) =>
    tables.reduce((n, t) => n + (inFlightBy.get(t) ?? 0), 0);

  /* A write blocked on a row lock fails fast (lock_timeout, migration 0190) and
     is retried a couple of times — see src/backend/lock-retry.ts for why 55P03
     alone is safe to re-run. #8. */

  const api = {
    async get(path) {
      return readOne(target(path, id));
    },

    /*
     * EVERY BOARD'S ISSUES (or phases) IN ONE READ, for the portfolio roll-up.
     * A plain select on the child table: RLS returns only the rows on boards the
     * caller may open, so the same access the sidebar already enforces scopes
     * this too. Light on purpose — no nested comments or history — because a
     * pulse is read from the row's own columns, and nesting every issue in the
     * tenant would be a fetch per row. Each row carries `parentId` = its board.
     */
    async readGroup(name) {
      const rows = await pageGroup(name);
      return rows.map(
        (r) => ({ ...toFields(r, name), parentId: r.board_id as string }) as WithParent,
      );
    },

    /*
     * ONLY the status moves inside the window — `to_status` set (a column change,
     * not a comment or an assignment) and `at` on or after `sinceMs`. Bounded by
     * the window, so a whole tenant's recent moves is a small read, but paged all
     * the same: a very busy week could still pass the 1000-row cap, and a move
     * dropped there is exactly the kind of silent gap the board history read was
     * fixed for. `at` is a timestamptz column, so the bound is an ISO string.
     */
    async readMovesSince(sinceMs) {
      const isoSince = new Date(sinceMs).toISOString();
      const PAGE = 1000;
      const rows: Rec[] = [];
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await client
          .from('activity')
          .select('*')
          .not('to_status', 'is', null)
          .gte('at', isoSince)
          .order('at', { ascending: true })
          .range(from, from + PAGE - 1);
        if (error) fail(error);
        const batch = (data ?? []) as Rec[];
        rows.push(...batch);
        if (batch.length < PAGE) break;
      }
      return rows.map(
        (r) => ({ ...toFields(r, 'activity'), parentId: r.issue_id as string }) as WithParent,
      );
    },

    async set(path, data) {
      const t = target(path, id);
      changing(tableFromTarget(t));
      const rec = data as Rec;
      if (t.kind === 'access') return writeAccess(rec);
      if (t.kind === 'role') return writePerson(t.email, rec, 'role');
      if (t.kind === 'profile') return writePerson(t.email, rec, 'profile');
      if (t.kind === 'boardRole') {
        const { error } = await client
          .from('board_roles')
          .upsert({ tenant_id: await tenantId(), board_id: t.board, email: t.email, role: rec.role },
                  { onConflict: 'tenant_id,board_id,email' });
        if (error) fail(error);
        return;
      }
      if (t.kind !== 'row') return fail({ message: `cannot write to the collection "${path}"` });
      const { comments, activity, members, groups, ...rest } = rec;
      /*
       * THE PATH IDENTIFIES THE ROW, AND MUST WIN.
       *
       * `t.where` carries the key already turned into a uuid — the app's
       * `audit/1786215462149-jf8si2` hashed the way every other id is. The
       * RECORD carries the same id as an ordinary field, unhashed, because the
       * app has always stored it inside the document as well.
       *
       * Spread after the columns, the raw string overwrote the hashed one and
       * Postgres refused: "invalid input syntax for type uuid". Spread last, the
       * path wins and the field is simply the same value said twice.
       */
      const tid0 = await tenantId();
      const { error } = await withLockRetry(() =>
        client
          .from(t.table)
          .upsert({ tenant_id: tid0, ...toColumns(asIds(rest, t.table), t.table), ...t.where })
      );
      if (error) fail(error);
      // Arrays handed in on a whole-document write become rows.
      if (t.table === 'issues') await replaceChildren(t.where.id as string, comments, activity);
      if (t.table === 'boards') await replaceBoardLists(t.where.id as string, members, groups);
    },

    async patch(path, data) {
      const t = target(path, id);
      changing(tableFromTarget(t));
      const rec = data as Rec;
      if (t.kind === 'access') return writeAccess(rec);
      if (t.kind === 'role') return writePerson(t.email, rec, 'role');
      if (t.kind === 'profile') return writePerson(t.email, rec, 'profile');
      if (t.kind === 'boardRole') {
        const { error } = await client
          .from('board_roles')
          .upsert({ tenant_id: await tenantId(), board_id: t.board, email: t.email, role: rec.role },
                  { onConflict: 'tenant_id,board_id,email' });
        if (error) fail(error);
        return;
      }
      if (t.kind !== 'row') return fail({ message: `cannot write to the collection "${path}"` });
      const { comments, activity, members, groups, ...rest } = rec;
      /*
       * The key is what the row is FOUND by, so setting it again is at best
       * pointless and at worst the unhashed version of itself — the same fault
       * as in `set` above, where it produced "invalid input syntax for uuid".
       */
      const cols = toColumns(asIds(rest, t.table), t.table);
      for (const k of Object.keys(t.where)) delete cols[k];
      if (Object.keys(cols).length) {
        /*
         * ── A WRITE THAT CHANGED NOTHING IS NOT A SUCCESS ──────────────────
         *
         * PostgREST answers an update that matched no rows with 204, exactly as
         * it answers one that matched ten. So a row hidden by a policy, or a
         * row addressed by an id that is not on the board named in the path,
         * comes back looking like it worked.
         *
         * That silence is what hid the fault of 19 August 2026 for two days.
         * The board on screen was showing another board's cards, so every write
         * was addressed to the right card on the wrong board, matched nothing,
         * and returned success. Vinnie could only describe it as "clicking add
         * doesn't do anything" — which is precisely all there was to see.
         *
         * `.select()` makes PostgREST return what it changed, so nothing can be
         * counted as done that was not done. It costs one round trip's worth of
         * rows and buys the difference between a refusal and a silence.
         */
        const { data: changed, error } = await withLockRetry(() =>
          where(client.from(t.table).update(cols).select('*'), t.where)
        );
        if (error) fail(error);
        if (!changed || changed.length === 0) {
          fail({
            message:
              `Nothing was changed. The ${t.table.replace(/s$/, '')} this was meant for ` +
              `could not be found, or the rules do not allow you to change it.`,
            code: 'changed-nothing',
          });
        }
      }
      if (t.table === 'issues' && (comments || activity))
        await replaceChildren(t.where.id as string, comments, activity);
      if (t.table === 'boards' && (members || groups))
        await replaceBoardLists(t.where.id as string, members, groups);
    },

    /*
     * A PATCH THAT ONLY LANDS IF THE ROW STILL LOOKS AS EXPECTED — a claim.
     *
     * The `guard` goes into the WHERE beside the id, so two callers racing to
     * move the same row out of one state cannot both win: whoever updates first
     * changes it, and the loser's WHERE no longer matches, so it touches nothing
     * and this returns false. Used to decide a board request exactly once — the
     * second admin is told it is already decided rather than repeating the work
     * (side effects included). Returns whether this call was the one that moved
     * it.
     */
    async patchIf(path, guard, data) {
      const t = target(path, id);
      changing(tableFromTarget(t));
      if (t.kind !== 'row') fail({ message: `cannot conditionally write to the collection "${path}"` });
      const cols = toColumns(asIds(data as Rec, (t as { table: string }).table), (t as { table: string }).table);
      for (const k of Object.keys((t as { where: Rec }).where)) delete cols[k];
      if (!Object.keys(cols).length) return false;
      const guardCols = toColumns(guard as Rec, (t as { table: string }).table);
      const { data: changed, error } = await withLockRetry(() =>
        where(
          where(client.from((t as { table: string }).table).update(cols).select('id'), (t as { where: Rec }).where),
          guardCols
        )
      );
      if (error) fail(error);
      return !!(changed && changed.length);
    },

    async remove(path) {
      const t = target(path, id);
      changing(tableFromTarget(t));
      if (t.kind === 'boardRole') {
        const { error } = await client
          .from('board_roles')
          .delete()
          .eq('email', t.email)
          .eq('board_id', t.board);
        if (error) fail(error);
        return;
      }
      if (t.kind !== 'row') return fail({ message: `cannot delete the collection "${path}"` });
      const { error } = await withLockRetry(() => where(client.from(t.table).delete(), t.where));
      if (error) fail(error);
    },

    /**
     * ADD TO AN ARRAY WITHOUT READING IT FIRST — which here is simply an
     * insert, and is the one method that gets strictly better. `arrayUnion`
     * existed because Firestore could not append without rewriting the whole
     * array, so two people commenting at once overwrote each other.
     */
    async append(path, field, values) {
      const t = target(path, id);
      if (t.kind !== 'row' || t.table !== 'issues')
        return fail({ message: `nothing to append to at "${path}"` });
      const tid = await tenantId();
      const table = field === 'comments' ? 'comments' : 'activity';
      /*
       * A COMMENT'S ID IS THE APP'S OWN STRING, and the column is a uuid.
       * `uid()` produces eight characters, and a comment written in the app
       * carries whatever it made — handed to Postgres it is refused as invalid
       * syntax. Hashed the same way every other id is, so the same comment is
       * the same row however often it is written.
       */
      const rows = values.map((v) => {
        const rec = toColumns(v as Rec, table);
        return {
          tenant_id: tid,
          issue_id: t.where.id,
          ...rec,
          ...(rec.id ? { id: id(table === 'comments' ? 'comment' : 'activity', String(rec.id)) } : {}),
        };
      });
      const { error } = await withLockRetry(() => client.from(table).insert(rows));
      if (error) fail(error);
    },

    async clearField(path, field) {
      const t = target(path, id);
      if (t.kind === 'boardRole') {
        const { error } = await client.from('board_roles').delete().eq('email', t.email).eq('board_id', t.board);
        if (error) fail(error);
        return;
      }
      if (t.kind !== 'row') return fail({ message: `nothing to clear at "${path}"` });
      const { error } = await where(client.from(t.table).update({ [field]: null }), t.where);
      if (error) fail(error);
    },

    async editComment(path, commentId, patch) {
      const t = target(path, id);
      if (t.kind !== 'row' || t.table !== 'issues')
        return fail({ message: `cannot edit a comment at "${path}"` });
      const cols = toColumns(patch as Rec, 'comments');
      /*
       * The card holds a comment's id as the stored uuid it was read back as, so
       * it is matched as-is — NOT re-hashed the way `append` hashes an app-made id
       * on the way in. Scoped to the issue as well as the id so a mistyped path
       * cannot reach another card's comment, and `.select()` so a row hidden by a
       * policy (someone editing words that are not theirs) is told apart from a
       * silent success — the same guard `patch` above explains at length.
       */
      const { data: changed, error } = await client
        .from('comments')
        .update(cols)
        .match({ tenant_id: await tenantId(), issue_id: t.where.id, id: commentId })
        .select('*');
      if (error) fail(error);
      if (!changed || changed.length === 0)
        fail({
          message:
            'Nothing was changed. The comment could not be found, or the rules do not allow you to change it.',
          code: 'changed-nothing',
        });
    },

    /*
     * A LIGHT REACTION, LEFT AND TAKEN BACK — migration 0143.
     *
     * A reaction is its own row keyed by the natural tuple, so these write it
     * directly rather than through a path: the id the generic machinery wants
     * does not exist here. `issueId` and `targetId` are already the stored ids
     * the card holds (a uuid and a feed-entry id), so neither is re-hashed. The
     * reactor is lower-cased to match me() in the rules and the stored author
     * convention. The seam adds tenant_id, exactly as every other write does.
     */
    async addReaction(issueId, targetId, emoji, reactor) {
      const { error } = await client.from('reactions').insert({
        tenant_id: await tenantId(),
        issue_id: issueId,
        target_id: targetId,
        emoji,
        reactor: reactor.toLowerCase(),
      });
      // Pressing a reaction you already left is a no-op, not a fault — the row is
      // already there (the primary key is the tuple), so a duplicate is success.
      if (error && error.code !== '23505') fail(error);
    },
    async removeReaction(issueId, targetId, emoji, reactor) {
      const { error } = await client.from('reactions').delete().match({
        tenant_id: await tenantId(),
        issue_id: issueId,
        target_id: targetId,
        emoji,
        reactor: reactor.toLowerCase(),
      });
      // Taking back one that is not there changes nothing and is not an error.
      if (error) fail(error);
    },

    /*
     * THE FILE STORE — #200, and the first thing here that is not a row.
     *
     * The bucket is private and the rules on it are the same ones the tables
     * use: `can_open(board_id)`, read out of the first folder of the key. So
     * these three do no checking of their own, exactly like every method above.
     * A caller that got a refusal got it from the database.
     */
    async putFile(key, file, type) {
      const { error } = await client.storage.from(FILES).upload(key, file, {
        contentType: type,
        /* A draft's file can be replaced; the rules refuse it once the document
           has been sent for signature, so this is not the check — it is what
           lets the allowed case work without a delete first. */
        upsert: true,
      });
      if (error) fail(error);
    },

    async fileUrl(key, seconds = 300) {
      const { data, error } = await client.storage.from(FILES).createSignedUrl(key, seconds);
      if (error) fail(error);
      if (!data?.signedUrl) return fail({ message: `no address came back for "${key}"` });
      return data.signedUrl;
    },

    async removeFile(key) {
      const { error } = await client.storage.from(FILES).remove([key]);
      if (error) fail(error);
    },

    watchDoc(path, on, onError) {
      const t = target(path, id);
      /* Stopped means silent — the same rule as watchCollection below, and for
         the same reason: the board document of the board you have just left is
         no more welcome than its cards. */
      let stopped = false;
      /* Numbered, waited on and checked against local changes for the same
         three reasons as the list listener below. A board document is smaller
         but it is read on the same events, and it went backwards the same way. */
      let issued = 0;
      let shown = 0;
      let waiting: ReturnType<typeof setTimeout> | undefined;
      /* This document is one table; a write to anything else can't have moved it. */
      const guardTables = [t.kind === 'row' ? t.table : 'people'];
      const read = async () => {
        const ticket = (issued += 1);
        const asAt = writesOn(guardTables);
        const quiet = inFlightOn(guardTables) === 0;
        try {
          const doc_ = await readOne(t);
          if (stopped || ticket < shown) return;
          if (!quiet || writesOn(guardTables) !== asAt) return soon();
          shown = ticket;
          on(doc_);
        } catch (e) {
          if (!stopped) onError?.(asBackendError(e));
        }
      };
      const soon = () => {
        if (stopped) return;
        clearTimeout(waiting);
        waiting = setTimeout(() => void read(), 60);
      };
      void read();
      const table = t.kind === 'row' ? t.table : 'people';
      const named = channelNamed(`doc:${path}`);
      const ch = client
        .channel(named)
        .on('postgres_changes', { event: '*', schema: 'public', table }, soon);
      listenFor(ch, named, soon, client);
      return () => {
        stopped = true;
        clearTimeout(waiting);
        /* Let go on purpose, so it is not an outage — see src/live.ts. */
        catchUp.delete(named);
        stopWatching(named);
        void client.removeChannel(ch);
      };
    },

    watchCollection(path, on, opts = {}) {
      const t = target(path, id);
      /*
       * ── A LISTENER THAT HAS BEEN STOPPED SAYS NOTHING MORE ────────────────
       *
       * Unsubscribing used to remove the realtime channel and nothing else, so
       * a read already in flight came back afterwards and handed a screen the
       * data of the board it had just left. Switching boards is exactly that:
       * stop the old listeners, start the new ones — and the old fetch, issued
       * before the switch, resolves a moment after it.
       *
       * WHAT IT LOOKED LIKE. On 19 August 2026 the test copy showed the Dosely
       * board — its name, its key, its counts — carrying PhotoSyncApp's cards.
       * The board document had arrived for Dosely and the issues had arrived
       * for the board before it. Vinnie found it as "on the Dosely board I
       * cannot add anything, clicking add doesn't do anything": every write
       * went to Dosely, addressed to cards that live on another board, so
       * PostgREST matched no rows and returned 204 — a success, changing
       * nothing, saying nothing.
       *
       * It is silent by construction, which is why it survived: the request is
       * well formed, the response is a success, and the only evidence is that
       * the screen does not move.
       */
      let stopped = false;
      /*
       * ── AND AN OLDER ANSWER NEVER REPLACES A NEWER ONE ────────────────────
       *
       * Two reads can be in the air at once, and nothing says they come back in
       * the order they went out. The one that arrives second wins, whether or
       * not it is the newer of the two — so the list can jump backwards.
       *
       * Each read takes a number on the way out. On the way back, anything
       * older than what the screen has already been shown is dropped.
       */
      let issued = 0;
      let shown = 0;
      let waiting: ReturnType<typeof setTimeout> | undefined;
      /* The last list this listener handed the screen. A targeted re-read (below)
         splices into it rather than rebuilding the board from scratch. */
      let current: WithId[] = [];
      /*
       * THE TABLES THIS LISTENER ACTUALLY READS — used both to listen (below) and
       * to scope the write-race guard, so an unrelated boot write does not throw
       * its reads away. A card carries its comments and history, a board its
       * people and groups, so those nested tables count as this listener's too.
       */
      const NESTED: Record<string, string[]> = {
        issues: ['issues', 'comments', 'activity'],
        boards: ['boards', 'board_people', 'board_groups'],
      };
      const tables =
        t.kind === 'rows'
          ? (NESTED[t.table] ?? [t.table])
          : t.kind === 'boardRoles'
            ? ['board_roles']
            : ['people'];
      /*
       * EVERY READ RUNS THE SAME GUARDS. `fetchRows` decides WHAT to read — the
       * whole board, or just the cards that changed — and this wraps it in the
       * ordering and own-write checks that are the same either way.
       */
      const runRead = async (fetchRows: () => Promise<WithId[]>) => {
        const ticket = (issued += 1);
        const asAt = writesOn(tables);
        const quiet = inFlightOn(tables) === 0;
        try {
          let rows = await fetchRows();
          /* Asked of the database above AND checked here — see readMany for why
             both. A forged answer is the case this second one exists for. */
          if (opts.where) rows = rows.filter((r) => r[opts.where!.field] === opts.where!.equals);
          /* Between asking and answering, this listener may have been let go. */
          if (stopped || ticket < shown) return;
          /* Or this browser changed one of THESE tables, which this answer cannot
             know about. Ask again rather than show it — see the counters above. */
          if (!quiet || writesOn(tables) !== asAt) return soon();
          shown = ticket;
          current = rows;
          on(rows);
        } catch (e) {
          if (stopped) return;
          opts.onError?.(asBackendError(e));
        }
      };
      const read = () => void runRead(() => readMany(t, opts.where));
      /*
       * JUST THE CARDS THAT CHANGED (#7b, 21 Oct). A realtime echo used to re-read
       * the WHOLE board — every card's comments and history — however small the
       * change. On a 200-card board that is the lag. Now the echo names the cards
       * it touched (below) and this re-reads only those, in the SAME board scope
       * as the full read, and splices them into the list already on screen:
       * updated cards are replaced where they sit (no reorder), genuinely-new ones
       * are appended, deleted ones and any that no longer match the board drop out.
       *
       * The DATABASE stays the source of truth — we re-read the changed rows, we
       * do NOT reconstruct them from the realtime payload (which, without REPLICA
       * IDENTITY FULL, does not even carry every column). The board-scope `where`
       * is kept, so a change echoed for another board's card reads back nothing
       * here and cannot leak in — the same guard the full read relies on.
       */
      const readIssuesByIds = async (ids: string[]): Promise<WithId[]> => {
        if (t.kind !== 'rows' || ids.length === 0) return [];
        const { data, error } = await where(client.from(t.table).select('*'), t.where).in('id', ids);
        if (error) fail(error);
        const nested = await nest((data ?? []) as Rec[], t.table);
        return nested.map((r) => toFields(r, t.table) as WithId);
      };
      const readSome = (upsert: string[], remove: Set<string>) =>
        void runRead(async () => {
          const fresh = await readIssuesByIds(upsert);
          const freshById = new Map(fresh.map((r) => [r.id as string, r]));
          /* Read AFTER the await, so we splice into the freshest list shown — a
             full read that landed meanwhile has already updated `current`. */
          const base = current;
          const absent = new Set(upsert.filter((i) => !freshById.has(i)));
          const existing = new Set(base.map((r) => r.id as string));
          const kept = base
            .filter((r) => !remove.has(r.id as string) && !absent.has(r.id as string))
            .map((r) => freshById.get(r.id as string) ?? r);
          const added = fresh.filter((r) => !existing.has(r.id as string));
          return [...kept, ...added];
        });
      /*
       * ONE CHANGE IS SEVERAL EVENTS. Moving a card into a sprint writes the
       * card and writes its history, and adding twenty writes forty — each one
       * announcing itself. A short wait collapses a burst into one read; it is
       * below the length of a blink, and the screen has already moved anyway —
       * the card is put in place the instant it is pressed, and this confirms it.
       *
       * What the burst touched is gathered here. `issues` events name the card by
       * its own id; `comments`/`activity` events name it by `issue_id`. A DELETE
       * whose payload cannot name the card, a reconnect (which arrives with no
       * payload), anything that is not the issues collection, or an echo before
       * the first full read has filled `current` — any of those falls back to a
       * full re-read, which is always correct, only slower.
       */
      type Echo = { eventType?: string; table?: string; new?: Rec; old?: Rec };
      const isIssues = t.kind === 'rows' && t.table === 'issues';
      const pendingUpsert = new Set<string>();
      const pendingRemove = new Set<string>();
      let needFull = false;
      const flush = () => {
        if (stopped) return;
        const upsert = [...pendingUpsert];
        const remove = new Set(pendingRemove);
        /* A burst touching more cards than fit one id-list is no cheaper to
           splice than to re-read whole — and `.in('id', …)` past that many ids
           outgrows the URL (the worry nest's chunking exists for). Re-read whole. */
        const whole = needFull || current.length === 0 || upsert.length > 150;
        needFull = false;
        pendingUpsert.clear();
        pendingRemove.clear();
        if (whole || (upsert.length === 0 && remove.size === 0)) return read();
        readSome(upsert, remove);
      };
      const schedule = () => {
        if (stopped) return;
        clearTimeout(waiting);
        waiting = setTimeout(() => flush(), 60);
      };
      /* The re-ask path and reconnects: force a full read, then schedule. */
      const soon = () => {
        needFull = true;
        schedule();
      };
      const noteChange = (payload?: Echo) => {
        if (stopped) return;
        if (!isIssues || !payload) {
          needFull = true;
        } else if (payload.table === 'issues') {
          if (payload.eventType === 'DELETE') {
            const gone = payload.old?.id as string | undefined;
            if (gone) pendingRemove.add(gone);
            else needFull = true;
          } else {
            const id = (payload.new?.id ?? payload.old?.id) as string | undefined;
            if (id) pendingUpsert.add(id);
            else needFull = true;
          }
        } else {
          /* comments / activity — the card is named by issue_id; a DELETE without
             it (no REPLICA IDENTITY FULL) can't be placed, so re-read whole. */
          const iid = (payload.new?.issue_id ?? payload.old?.issue_id) as string | undefined;
          if (iid) pendingUpsert.add(iid);
          else needFull = true;
        }
        schedule();
      };
      void runRead(() => readMany(t, opts.where));
      /*
       * A COLLECTION IS NOT ALWAYS ONE TABLE, and listening to one was why a
       * comment did not appear until the page was reloaded.
       *
       * An issue is read with its comments and its history nested inside it, and
       * those are rows in tables of their own — so a new comment changes nothing
       * in `issues` and fired nothing at all. Same for a board: who may open it
       * lives in board_people and board_groups.
       *
       * Whatever is nested INTO a collection has to be listened to alongside it,
       * or the screen is only as current as the last reload. `tables` (hoisted
       * above, where the write-race guard also reads it) is exactly that set.
       */
      const named = channelNamed(`list:${path}`);
      const ch = tables.reduce(
        (c, table) =>
          c.on('postgres_changes', { event: '*', schema: 'public', table }, (payload: Echo) =>
            noteChange(payload)
          ),
        client.channel(named)
      );
      listenFor(ch, named, soon, client);
      return () => {
        stopped = true;
        clearTimeout(waiting);
        catchUp.delete(named);
        stopWatching(named);
        void client.removeChannel(ch);
      };
    },

    /**
     * The collection-group idea disappears on Postgres — "every `on`
     * subcollection wherever it appears" is one table, and this is a query.
     */
    watchGroup(name, on, onError) {
      /*
       * THE CROSS-BOARD CARD CACHE, KEPT LIVE — for the Portfolio and My Work.
       *
       * `readGroup('issues')` reads every card the viewer may see in one go; this
       * is that read kept current. A change to any card on any board fires a
       * debounced re-read, so assigning a card to yourself, moving it, or a status
       * change shows AT ONCE without a reload — the same "a change fires a re-read"
       * shape watchCollection uses for a single board, at tenant scope.
       *
       * The store subscribes to this ONLY WHILE a screen that needs it is mounted
       * (My Work on mount, dropped on unmount), so the tenant-wide listener and
       * its re-reads exist only while someone is looking — not a standing cost.
       * A longer debounce than the per-board watcher (300ms vs 60ms) collapses a
       * burst — assigning several cards at once — into a single read.
       */
      if (name === 'issues') {
        let stopped = false;
        let waiting: ReturnType<typeof setTimeout> | undefined;
        const read = async () => {
          if (stopped) return;
          try {
            // Paged past the 1000-row cap, like readGroup — this live cache
            // feeds the very same portfolio/My-Work figures, so an unpaged
            // re-read would silently re-truncate them after every card change.
            const rows = await pageGroup('issues');
            if (stopped) return;
            on(
              rows.map(
                (r) => ({ ...toFields(r, 'issues'), parentId: r.board_id as string }) as WithParent
              )
            );
          } catch (e) {
            if (!stopped) onError?.(asBackendError(e));
          }
        };
        const soon = () => {
          if (stopped) return;
          clearTimeout(waiting);
          waiting = setTimeout(() => void read(), 300);
        };
        void read();
        const named = channelNamed('group:issues');
        const ch = client
          .channel(named)
          .on('postgres_changes', { event: '*', schema: 'public', table: 'issues' }, soon);
        listenFor(ch, named, soon, client);
        return () => {
          stopped = true;
          clearTimeout(waiting);
          catchUp.delete(named);
          stopWatching(named);
          void client.removeChannel(ch);
        };
      }
      if (name !== 'on') {
        onError?.(asBackendError({ message: `no group named "${name}"` }));
        return () => {};
      }
      const read = async () => {
        try {
          const { data, error } = await client.from('board_roles').select('*');
          if (error) throw error;
          on(
            ((data ?? []) as Rec[]).map(
              (r) => ({ id: r.board_id, role: r.role, parentId: r.email }) as WithParent
            )
          );
        } catch (e) {
          onError?.(asBackendError(e));
        }
      };
      void read();
      const named = channelNamed('group:on');
      const ch = client
        .channel(named)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'board_roles' }, () => void read());
      listenFor(ch, named, () => void read(), client);
      return () => {
        catchUp.delete(named);
        stopWatching(named);
        void client.removeChannel(ch);
      };
    },

    /**
     * ONE STATEMENT, WHICH IS WHY THIS IS NOT A TRANSACTION ANY MORE.
     *
     * `take_next_number` increments and returns in one go, so there is nothing
     * to retry and nothing to serialise. It also runs as its owner, which is the
     * point: it checks that the caller may raise an issue on the board — a
     * REVIEWER may — and then moves the counter on, which a reviewer could not do
     * themselves. Before this, the app patched the board to advance the counter,
     * was refused, and quietly handed the same number out twice.
     */
    /**
     * A row for me in `people`, which is what membership IS here.
     *
     * The policy needs my workspace to show me anything, and my workspace is
     * found from my own row — so a stranger matches nothing and sees nothing,
     * without any of it being an error. That is the whole reason this has to be
     * asked rather than inferred.
     */
    async amIAMember() {
      // Asked only once the sign-in is attached — see sessionAttached, and #297.
      await sessionAttached();
      let { data, error } = await client.from('people').select('email').limit(1);
      /*
       * ONE MORE TIME BEFORE GIVING UP, and on any failure rather than on a
       * shape of failure.
       *
       * A 401 here arrives as several different codes depending on how the
       * token was wrong, and matching them by hand is how a retry comes to
       * cover three of the four cases. Retrying anything costs one request in
       * the rare case it fails, and it cannot turn a refusal into a wrong
       * answer: if the second attempt fails too, this still throws exactly as
       * it did before.
       *
       * Note what is NOT being retried past: a stranger who IS signed in gets
       * 200 and no rows, never an error. So an error here is always about the
       * token rather than about membership.
       */
      if (error) {
        await sessionAttached();
        ({ data, error } = await client.from('people').select('email').limit(1));
      }
      // A blip is not an answer. Saying "not a member" because the network
      // hiccuped would lock somebody out of their own workspace.
      if (error) fail(error);
      return (data ?? []).length > 0;
    },

    async nextNumber(boardPath) {
      const t = target(boardPath, id);
      if (t.kind !== 'row' || t.table !== 'boards') return fail({ message: `not a board: "${boardPath}"` });
      const { data, error } = await client.rpc('take_next_number', { board: t.where.id });
      if (error) fail(error);
      return data as number;
    },

    /*
     * ONE DIGEST ROW PER RECIPIENT for a bulk card change (project-bulk-card-changes).
     * Through an RPC, because a browser has no insert on notifications — a feed row
     * written FOR another person is a SECURITY DEFINER act (0105/0106), the same
     * reason every other call-out is raised in the database. The function takes the
     * already-grouped digests and writes exactly one row for each, deriving the
     * tenant and the actor from the caller's own session (never trusting the client
     * for who did it), and resolving each digest's board the same way take_next_number
     * resolves a board. See the migration note handed to the orchestrator —
     * raise_bulk_notification + the 'assigned_bulk'/'moved_bulk' feed kinds.
     */
    async bulkNotify(digests) {
      if (!digests.length) return;
      const { error } = await client.rpc('raise_bulk_notification', {
        digests: digests.map((d) => ({
          owner: d.owner,
          kind: d.kind,
          board: d.boardId,
          refs: d.refs,
          titles: d.titles,
          count: d.count,
        })),
      });
      if (error) fail(error);
    },

    /*
     * One last-signed-in time per person, for the People roster. Through a
     * function because sessions is private even from admins (0002) — the RPC
     * (migration 0210) is self-gated to admins and hands back only an address and
     * a timestamp, never a device or a place. A non-admin gets nothing back.
     */
    async lastSignInTimes() {
      await sessionAttached();
      const { data, error } = await client.rpc('last_sign_in_times');
      if (error) fail(error);
      return ((data ?? []) as { email: string; at: string | null }[])
        .map((r) => ({ email: String(r.email).toLowerCase(), at: r.at ? Date.parse(r.at) : 0 }))
        .filter((r) => r.at > 0);
    },

    /*
     * Through a function, because this is the one write that crosses out of the
     * caller's workspace and the rules exist to stop a browser doing that. It
     * answers with a reference and nothing else — there is no read path.
     */
    async reportFeedback(report) {
      const { data, error } = await client.functions.invoke('feedback', { body: report });
      if (error) {
        /*
         * The function's own words, not the library's. `invoke` reports every
         * non-2xx as "Edge Function returned a non-2xx status code", which
         * would replace the one sentence that tells somebody what to do with
         * one that tells them nothing.
         */
        let said = error.message;
        try {
          const body = await (error as { context?: Response }).context?.json();
          if (body?.error) said = body.error;
        } catch {
          // Keep the library's message; it is all there is.
        }
        fail({ message: said });
      }
      return String((data as { ref?: string })?.ref ?? '');
    },

    /*
     * Through a function for two reasons a browser cannot be trusted with:
     * marking a comment as one somebody in ANOTHER workspace may read, and
     * sending mail as us. A key in a browser that can send email from
     * flightdeck.build is a phishing kit.
     */
    async answerReport(issue, text, about) {
      const { data, error } = await client.functions.invoke('reply', {
        body: { issue, text, ...(about ? { about } : {}) },
      });
      if (error) {
        let said = error.message;
        try {
          const body = await (error as { context?: Response }).context?.json();
          if (body?.error) said = body.error;
        } catch {
          // Keep the library's message; it is all there is.
        }
        fail({ message: said });
      }
      const said = data as { emailed?: boolean; why?: string; alreadyTold?: boolean };
      return { emailed: said?.emailed === true, why: said?.why, alreadyTold: said?.alreadyTold };
    },

    /*
     * The same shape as answerReport, and for the same reason — sending mail as
     * us is not a thing a browser may do. The token goes over TLS to our own
     * function, which checks its hash against the row before it will post
     * anything, and never writes it down.
     */
    async sendSigningLink(signoff, token) {
      const { data, error } = await client.functions.invoke('send-signing-link', {
        body: { signoff, token },
      });
      if (error) {
        let said = error.message;
        try {
          const body = await (error as { context?: Response }).context?.json();
          if (body?.error) said = body.error;
        } catch {
          // Keep the library's message; it is all there is.
        }
        /* NOT A THROW. The link exists whatever the mail did. */
        return { emailed: false, why: said };
      }
      const said = data as { emailed?: boolean; to?: string; why?: string };
      return { emailed: said?.emailed === true, to: said?.to, why: said?.why };
    },

    /*
     * NOTIFICATION SWITCHES — read straight from `notification_prefs` (0126). The
     * rules already scope a read to the person's own rows, or, for an admin,
     * their workspace, so there is nothing here a function would add. Absence
     * means on, so only the muted-or-locked rows come back.
     */
    async notificationPrefs(email) {
      await sessionAttached();
      let q = client.from('notification_prefs').select('kind, muted, locked_by');
      /* With an address this reads a member's rows (admin only, by the rules);
         without one, the person's own — the read policy decides which is allowed. */
      if (email) q = q.eq('email', email.toLowerCase());
      const { data, error } = await q;
      if (error) fail(error);
      return (data ?? []).map((r) => ({
        kind: String((r as { kind: string }).kind),
        muted: (r as { muted: boolean }).muted === true,
        lockedBy: String((r as { locked_by?: string }).locked_by ?? ''),
      }));
    },

    async setNotificationMuted(kind, muted) {
      const tid = await tenantId();
      const { data: u } = await client.auth.getUser();
      const email = (u.user?.email ?? '').toLowerCase();
      if (!email) fail({ code: '401', message: 'Sign in first.' });
      if (muted) {
        /* One row per (workspace, person, kind); a second mute is not a second
           row. The lock guard (0126) forces muted false on a locked kind, so
           this quietly no-ops on one an admin insisted on. */
        const { error } = await client
          .from('notification_prefs')
          .upsert({ tenant_id: tid, email, kind, muted: true }, { onConflict: 'tenant_id,email,kind' });
        if (error) fail(error);
      } else {
        /* On is the absence of a row. The delete rule refuses a locked row, so a
           person cannot clear a kind an admin locked on. */
        const { error } = await client
          .from('notification_prefs')
          .delete()
          .eq('tenant_id', tid)
          .eq('email', email)
          .eq('kind', kind);
        if (error) fail(error);
      }
    },

    async adminSetNotification(email, kind, on, lock) {
      const tid = await tenantId();
      const e = email.toLowerCase();
      if (on) {
        const { data: u } = await client.auth.getUser();
        const me = (u.user?.email ?? '').toLowerCase();
        /* Turn it on for the member, and lock it to this admin when asked. The
           guard forces muted false whenever locked_by is set, so this is on. */
        const { error } = await client
          .from('notification_prefs')
          .upsert(
            { tenant_id: tid, email: e, kind, muted: false, locked_by: lock ? me : '' },
            { onConflict: 'tenant_id,email,kind' }
          );
        if (error) fail(error);
      } else {
        /* Clear the admin's row and return the member to their own default. */
        const { error } = await client
          .from('notification_prefs')
          .delete()
          .eq('tenant_id', tid)
          .eq('email', e)
          .eq('kind', kind);
        if (error) fail(error);
      }
    },

    /*
     * Read straight from the table — the rules already say only this workspace
     * may see it, so there is nothing here a function would add.
     */
    async whoReported(issue) {
      const { data } = await client
        .from('reports')
        .select('reported_by')
        .eq('issue_id', issue)
        .maybeSingle();
      return String((data as { reported_by?: string } | null)?.reported_by ?? '');
    },

    async myReports() {
      const { data, error } = await client.functions.invoke('my-reports', { body: {} });
      /*
       * A LIST NOBODY ASKED FOR OUT LOUD. This is drawn on a screen somebody
       * opened, not in answer to a button, so a failure here should leave the
       * screen saying it could not load rather than throwing into a render.
       */
      if (error) return [];
      return ((data as { reports?: ReportedByMe[] })?.reports ?? []) as ReportedByMe[];
    },

    async importHolidays(country: string) {
      // A deliberate button-press, so a failure is surfaced (rejects) rather than
      // swallowed — the screen shows "couldn't reach the holiday service" and the
      // person can still add holidays by hand.
      const { data, error } = await client.functions.invoke('holidays', { body: { country } });
      if (error) throw new Error('The holiday service could not be reached. Try again, or add holidays by hand.');
      const payload = data as { holidays?: Holiday[]; error?: string } | null;
      if (payload?.error) throw new Error(payload.error);
      return (payload?.holidays ?? []) as Holiday[];
    },

    /*
     * THE STAFF CONSOLE IS NOT PART OF THIS SEAM, deliberately.
     *
     * It lived here, as three optional methods, and that put the operator's
     * code into the bundle every customer downloads — hidden behind a flag,
     * which hides a button rather than a file. It now sits in src/admin/ops.ts,
     * imported only by the portal at its own address and built separately.
     *
     * Nothing was lost by moving it: none of it was ever a customer feature,
     * and the seam exists so the app can talk to either database — running the
     * business is neither.
     */
  } satisfies Backend;

  /*
   * A CHANGE IS OVER WHEN ITS ANSWER COMES BACK, however it comes back.
   *
   * Wrapped here rather than written into each one: `set`, `patch`, `patchIf`
   * and `remove` each have a dozen ways out, and a single one that forgot to
   * say it had finished would leave a change counted as travelling for ever —
   * and every listener silent from then on, which is a worse fault than the
   * flicker. Every method that calls `changing()` must be wrapped here; forget
   * one and it counts up without ever counting down. `patchIf` was that one:
   * it began every claim and approval — moving a sign-off from pending — but
   * was left out of this list, so the first approval anyone made silenced
   * every live view in the app until the page was reloaded.
   *
   * A REFUSED change still ends. `.finally` is the point.
   */
  const settles =
    <A extends unknown[], R>(f: (...a: A) => Promise<R>) =>
    (...a: A): Promise<R> =>
      f(...a).finally(() => changed(tableOf(a[0] as string)));

  return {
    ...api,
    set: settles(api.set),
    patch: settles(api.patch),
    patchIf: settles(api.patchIf),
    remove: settles(api.remove),
  };

  /** Comments and activity written as a whole array become the rows for it. */
  async function replaceChildren(issueId: string, comments?: unknown, activity?: unknown) {
    const tid = await tenantId();
    for (const [table, list] of [
      ['comments', comments],
      ['activity', activity],
    ] as const) {
      if (!Array.isArray(list)) continue;
      await withLockRetry(() => client.from(table).delete().eq('issue_id', issueId));
      if (list.length) {
        const childRows = list.map((v) => {
          const rec = toColumns(v as Rec, table);
          return {
            tenant_id: tid,
            issue_id: issueId,
            ...rec,
            ...(rec.id ? { id: id(table === 'comments' ? 'comment' : 'activity', String(rec.id)) } : {}),
          };
        });
        await withLockRetry(() => client.from(table).insert(childRows));
      }
    }
  }
}
